import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/icons';
import { Footer } from '../components/Footer';
import Header from '../components/Header';
import CosmicWaves from '../components/CosmicWaves';

const EnglishLandingPage: React.FC = () => {
  // Statistics data
  const statistics = [
    { id: 3, label: 'Processed Records', value: '1M+' },
    { id: 4, label: 'Clinical Guidelines', value: '2000+' },
    { id: 5, label: 'Time Saved', value: '80%' },
    { id: 7, label: 'Medical Literature', value: '20M+' }
  ];

  // Specialty AI Assistants
  const specialtyAIAssistants = [
    {
      category: "Cardiovascular AI Assistant",
      icon: "Favorite",
      description: "Trained on ACC/AHA, ESC and other authoritative guidelines, focusing on cardiovascular disease diagnosis and treatment.",
      features: [
        "Real-time interpretation of ECG, ultrasound results",
        "Intelligent warning of heart failure risk",
        "Atrial fibrillation & coronary disease management",
        "Interventional surgery decision support"
      ],
      gradient: "from-blue-500 to-indigo-600"
    },
    {
      category: "Oncology AI Assistant",
      icon: "Science",
      description: "Deeply integrated with NCCN, ESMO guidelines, supporting precision anti-tumor treatment.",
      features: [
        "MDT consultation plan generation",
        "Personalized treatment recommendations",
        "Clinical trial matching & screening",
        "Targeted drug indication analysis"
      ],
      gradient: "from-indigo-500 to-purple-600"
    },
    {
      category: "Endocrinology AI Assistant",
      icon: "MonitorHeart",
      description: "Based on ADA and other guidelines, focusing on metabolic disease management.",
      features: [
        "Personalized glucose management plans",
        "Intelligent thyroid nodule grading",
        "Metabolic syndrome risk assessment",
        "Endocrine disorder diagnostic advice"
      ],
      gradient: "from-purple-500 to-pink-600"
    },
    {
      category: "Respiratory AI Assistant",
      icon: "Air",
      description: "Integrated with GOLD, GINA guidelines, providing respiratory disease support.",
      features: [
        "Lung function test interpretation",
        "Asthma/COPD personalized treatment",
        "Respiratory infection differentiation",
        "Lung imaging AI-assisted diagnosis"
      ],
      gradient: "from-teal-500 to-emerald-600"
    },
    {
      category: "Gastroenterology AI Assistant",
      icon: "LocalHospital",
      description: "Based on AASLD guidelines, focusing on digestive system diseases.",
      features: [
        "Endoscopy report generation",
        "Liver disease treatment planning",
        "IBD personalized recommendations",
        "Digestive tract tumor screening"
      ],
      gradient: "from-orange-500 to-red-600"
    },
    {
      category: "Neurology AI Assistant",
      icon: "Psychology",
      description: "Integrated with international neurology guidelines for professional decision support.",
      features: [
        "Headache diagnostic path recommendations",
        "Stroke thrombolysis assessment",
        "Parkinson's personalized treatment",
        "Epilepsy seizure warning analysis"
      ],
      gradient: "from-slate-600 to-slate-800"
    }
  ];

  // Problems and solutions data
  const problems = [
    {
      id: 1,
      problem: "Clinical Overload",
      problemDetail: "Doctors handle 40+ cases daily, with 60% of time spent on documentation. Traditional HIS mobile functionality is weak.",
      solutionTitle: "Smart Clinical Workflow",
      solutionDetails: [
        "Instant access to reports and imaging on mobile",
        "Automated pending orders and task aggregation",
        "Voice-to-text structured medical records",
        "Real-time department collaboration dashboards"
      ],
      icon: "Smartphone"
    },
    {
      id: 2,
      problem: "Quality Control Challenges",
      problemDetail: "Over 30% of records lack key elements. Traditional QC is reactive and standards are inconsistent.",
      solutionTitle: "Real-time AI QC System",
      solutionDetails: [
        "Automatic validation during consultation",
        "Smart prompts for missing information",
        "Detection of order-diagnosis contradictions",
        "Built-in 3000+ clinical pathway QC points"
      ],
      icon: "Checklist"
    },
    {
      id: 3,
      problem: "Decision Support Gap",
      problemDetail: "Complex cases require excessive research. Primary care resources are limited and updates are slow.", 
      solutionTitle: "Evidence-Based Decision Engine",
      solutionDetails: [
        "AI-assisted differential diagnosis trees",
        "Automatic clinical trial matching",
        "Virtual multidisciplinary consultation advice",
        "Success-based plan optimization"
      ],
      icon: "Forum"
    },
    {
      id: 4,
      problem: "Fragmented Management",
      problemDetail: "40% loss to follow-up. Chronic disease compliance is below 50% due to manual tracking reliance.",
      solutionTitle: "Full-Cycle Patient Care",
      solutionDetails: [
        "AI-generated disease milestone timelines",
        "Automated follow-up plan configuration",
        "Risk warning dashboards for abnormal metrics",
        "Patient self-service portal for reminders"
      ],
      icon: "Timeline"
    }
  ];

  const advantages = [
    {
      id: 1,
      title: "Intelligent Diagnosis Engine",
      icon: "PrecisionManufacturing",
      comparison: {
        traditional: {
          title: "Traditional",
          points: [
            "Manual review taking 40+ minutes",
            "Reliant on subjective experience",
            "Lagging knowledge application"
          ]
        },
        current: {
          title: "Agilis AI",
          points: [
            "AI analysis completed in 3 minutes",
            "Standardized evidence-based support",
            "Real-time medical knowledge updates"
          ]
        }
      }
    },
    {
      id: 2,
      title: "Clinical Collaboration",
      icon: "Groups",
      comparison: {
        traditional: {
          title: "Traditional",
          points: [
            "48h+ wait for offline consultations",
            "Low inter-department efficiency",
            "Information transmission gaps"
          ]
        },
        current: {
          title: "Agilis AI",
          points: [
            "Remote response within 2 hours",
            "Cross-department real-time platform",
            "Seamless treatment data sync"
          ]
        }
      }
    }
  ];

  const coreFeatures = [
    {
      id: 1,
      title: "Document Recognition",
      icon: "DocumentScanner",
      description: "Intelligent recognition of medical docs with key info extraction.",
      features: [
        "Supports 20+ document types",
        "Paper, screen, and device support",
        "95%+ OCR accuracy",
        "Automatic archival & filing"
      ],
      gradient: "from-blue-600 to-indigo-700"
    },
    {
      id: 2,
      title: "AI Decision Support",
      icon: "Lightbulb",
      description: "Intelligent condition analysis to assist clinical decisions.",
      features: [
        "Differential diagnosis trees",
        "Personalized treatment paths",
        "Medication rationality analysis",
        "Prognosis & risk prediction"
      ],
      gradient: "from-indigo-600 to-purple-700"
    },
    {
      id: 3,
      title: "AI Information Flow",
      icon: "Timeline",
      description: "Building continuous and complete patient progress records.",
      features: [
        "Multi-channel data collection",
        "Smart information extraction",
        "Scenario-based information views",
        "Complete hospital-to-home records"
      ],
      gradient: "from-purple-600 to-pink-700"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header currentPage="english" />

      <div className="pt-16">
        {/* Hero Section */}
        <div className="relative overflow-hidden min-h-[600px] flex items-center">
          <div className="absolute inset-0 z-0">
            <CosmicWaves speed={0.05} amplitude={0.8} frequency={1.0} starDensity={0.5} />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
            <div className="text-center animate-fadeIn">
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8">
                <span className="relative flex h-2 w-2 mr-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <p className="text-sm font-medium text-blue-50 tracking-wide uppercase">Next-Generation Intelligent Clinical Workstation</p>
              </div>
              <h1 className="text-5xl font-extrabold tracking-tight text-white sm:text-6xl lg:text-7xl mb-8">
                Agilis AI Clinical Solutions
              </h1>
              <p className="mt-6 text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                Empowering healthcare professionals with intelligent tools that streamline workflows and save 80% of documentation time. Enhancing expertise, not replacing it.
              </p>
              <div className="mt-12 flex flex-col sm:flex-row justify-center gap-6">
                <Link
                  to="/register"
                  className="group inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-primary-700 bg-white rounded-2xl shadow-xl hover:shadow-primary-500/25 hover:scale-105 transition-all duration-300"
                >
                  Start Free Trial
                  <Icon iconName="ArrowForward" size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-primary-600/30 backdrop-blur-md border-2 border-white/30 rounded-2xl hover:bg-white/10 transition-all duration-300"
                >
                  Book a Demo
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div className="relative -mt-12 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
              {statistics.map((stat, index) => (
                <div key={stat.id} 
                  className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 text-center animate-fadeIn"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="text-4xl font-black text-primary-600 mb-2">{stat.value}</div>
                  <div className="text-sm font-bold text-slate-500 uppercase tracking-widest">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Core Features Section */}
        <div className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 animate-fadeIn">
              <h2 className="text-sm font-black text-primary-600 uppercase tracking-[0.3em] mb-4">Core Capabilities</h2>
              <h3 className="text-4xl font-bold text-slate-900 sm:text-5xl">Feature Modules</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {coreFeatures.map((feature, index) => (
                <div key={feature.id} 
                  className="group relative bg-white rounded-3xl border border-slate-100 p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 animate-fadeIn"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className={`inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br ${feature.gradient} text-white shadow-lg mb-8 group-hover:scale-110 transition-transform duration-500`}>
                    <Icon iconName={feature.icon} size={32} />
                  </div>
                  <h4 className="text-2xl font-bold text-slate-900 mb-4">{feature.title}</h4>
                  <p className="text-slate-600 mb-8 leading-relaxed">{feature.description}</p>
                  <ul className="space-y-4">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-center text-slate-700 font-medium">
                        <Icon iconName="CheckCircle" size={20} className="text-green-500 mr-3 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Specialty Assistants Section */}
        <div className="py-24 bg-slate-900 overflow-hidden relative">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center mb-20">
              <h2 className="text-sm font-black text-primary-400 uppercase tracking-[0.3em] mb-4">Specialized AI</h2>
              <h3 className="text-4xl font-bold text-white sm:text-5xl">Specialty AI Assistants</h3>
              <p className="mt-6 text-xl text-slate-400 max-w-3xl mx-auto">
                Deeply integrated with authoritative guidelines for precise diagnostic support.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {specialtyAIAssistants.map((specialty, index) => (
                <div key={index} 
                  className="group bg-slate-800/50 backdrop-blur-sm border border-slate-700 p-8 rounded-3xl hover:bg-slate-800 transition-all duration-300"
                >
                  <div className={`inline-flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br ${specialty.gradient} text-white mb-6 group-hover:rotate-12 transition-transform`}>
                    <Icon iconName={specialty.icon} size={24} />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3">{specialty.category}</h4>
                  <p className="text-slate-400 text-sm mb-6 line-clamp-2">{specialty.description}</p>
                  <ul className="space-y-3">
                    {specialty.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-slate-300">
                        <Icon iconName="Check" size={16} className="text-primary-400 mr-2 mt-0.5 shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Problems & Solutions Section */}
        <div className="py-24 bg-slate-100/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-sm font-black text-primary-600 uppercase tracking-[0.3em] mb-4">Solutions</h2>
              <h3 className="text-4xl font-bold text-slate-900 sm:text-5xl">Addressing 4 Major Pain Points</h3>
            </div>
            <div className="space-y-8">
              {problems.map((item, index) => (
                <div key={item.id} 
                  className={`flex flex-col lg:flex-row bg-white rounded-[2.5rem] overflow-hidden border border-slate-200/60 shadow-sm hover:shadow-2xl transition-all duration-500 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className="lg:w-1/2 p-12 bg-white">
                    <div className="flex items-center mb-6">
                      <div className="h-14 w-14 rounded-2xl bg-primary-50 text-primary-600 flex items-center justify-center mr-4">
                        <Icon iconName={item.icon} size={28} />
                      </div>
                      <h4 className="text-3xl font-bold text-slate-900">{item.problem}</h4>
                    </div>
                    <p className="text-lg text-slate-600 leading-relaxed">{item.problemDetail}</p>
                  </div>
                  <div className="lg:w-1/2 p-12 bg-gradient-to-br from-slate-900 to-primary-900 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-8 opacity-10">
                      <Icon iconName={item.icon} size={200} className="text-white" />
                    </div>
                    <div className="relative z-10">
                      <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-white text-sm font-bold mb-8">
                        {item.solutionTitle}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {item.solutionDetails.map((detail, idx) => (
                          <div key={idx} className="flex items-start text-white/90 font-medium">
                            <Icon iconName="MdCheckCircle" size={24} className="text-green-400 mr-3 shrink-0" />
                            {detail}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Advantages Comparison */}
        <div className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-sm font-black text-primary-600 uppercase tracking-[0.3em] mb-4">Comparison</h2>
              <h3 className="text-4xl font-bold text-slate-900 sm:text-5xl">Why Choose Agilis AI</h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {advantages.map((advantage) => (
                <div key={advantage.id} className="bg-slate-100/40 rounded-[3rem] border border-slate-200/60 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-r from-slate-900 to-primary-900 p-8 flex items-center justify-between">
                    <div className="flex items-center">
                      <Icon iconName={advantage.icon} size={32} className="text-white mr-4" />
                      <h4 className="text-2xl font-bold text-white">{advantage.title}</h4>
                    </div>
                  </div>
                  <div className="p-10 grid grid-cols-1 sm:grid-cols-2 gap-10">
                    <div className="space-y-6">
                      <h5 className="flex items-center text-slate-400 font-bold uppercase tracking-widest text-xs">
                        <Icon iconName="History" size={16} className="mr-2" />
                        {advantage.comparison.traditional.title}
                      </h5>
                      <ul className="space-y-4">
                        {advantage.comparison.traditional.points.map((point, idx) => (
                          <li key={idx} className="flex items-start text-sm text-slate-500 italic">
                            <Icon iconName="Remove" size={18} className="text-slate-300 mr-2 shrink-0 mt-0.5" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-6">
                      <h5 className="flex items-center text-primary-600 font-bold uppercase tracking-widest text-xs">
                        <Icon iconName="Bolt" size={16} className="mr-2" />
                        {advantage.comparison.current.title}
                      </h5>
                      <ul className="space-y-4">
                        {advantage.comparison.current.points.map((point, idx) => (
                          <li key={idx} className="flex items-start text-sm text-slate-900 font-bold">
                            <Icon iconName="CheckCircle" size={18} className="text-green-500 mr-2 shrink-0 mt-0.5" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-slate-900 to-primary-900 rounded-[4rem] p-12 lg:p-20 relative overflow-hidden text-center lg:text-left">
              <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
              <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-16">
                <div className="lg:w-1/2">
                  <h3 className="text-4xl font-bold text-white mb-8">Enterprise Data Security</h3>
                  <p className="text-xl text-slate-300 leading-relaxed mb-12">
                    Utilizing AES-256 encryption, TLS 1.3, local storage, and private cloud options to ensure patient data integrity.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10">
                      <Icon iconName="Lock" size={24} className="text-amber-500 mb-4" />
                      <h5 className="text-white font-bold mb-2">End-to-End Encryption</h5>
                      <p className="text-slate-400 text-sm">Full protection for data in transit and at rest</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10">
                      <Icon iconName="Shield" size={24} className="text-amber-500 mb-4" />
                      <h5 className="text-white font-bold mb-2">Privacy Masking</h5>
                      <p className="text-slate-400 text-sm">Automatic sensitive data de-identification</p>
                    </div>
                  </div>
                </div>
                <div className="lg:w-1/3 relative">
                  <div className="relative z-10 bg-white p-8 rounded-3xl shadow-2xl">
                    <Icon iconName="VerifiedUser" size={80} className="text-amber-600 mx-auto mb-6" />
                    <h4 className="text-2xl font-black text-slate-900 text-center mb-4">Compliance</h4>
                    <p className="text-slate-500 text-center">Certified healthcare data privacy protection standards</p>
                  </div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-primary-500/20 blur-[100px] rounded-full" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-24 bg-slate-100/30 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative bg-gradient-to-br from-primary-700 to-primary-900 rounded-[3rem] p-12 lg:p-24 text-center overflow-hidden">
              <div className="absolute inset-0 opacity-[0.1]" style={{ backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
              <div className="relative z-10 animate-fadeIn">
                <h2 className="text-4xl font-bold text-white sm:text-6xl mb-8">Ready to enhance clinical efficiency?</h2>
                <p className="text-xl text-primary-100 mb-12 max-w-2xl mx-auto font-medium">
                  Join leading healthcare teams today and experience the transformation with Agilis AI.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-6">
                  <Link
                    to="/register"
                    className="px-10 py-5 bg-white text-primary-700 text-xl font-bold rounded-2xl hover:scale-105 transition-transform shadow-2xl"
                  >
                    Start Free Now
                  </Link>
                  <Link
                    to="/home-care"
                    className="px-10 py-5 bg-primary-700/50 text-white text-xl font-bold rounded-2xl hover:bg-primary-700 border border-white/20 transition-all backdrop-blur-md"
                  >
                    Switch to Patient Mode
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default EnglishLandingPage;
